import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { api } from '../../../api.js';
import { useAuth } from '../../../auth/AuthProvider.js';
import type { SalesReceipt } from '../../../types/bookkeeping.js';

const STATUS_CLASSES: Record<string, string> = {
  paid: 'bg-green-100 text-green-700',
  void: 'bg-slate-100 text-slate-500',
};

export function SalesReceipts() {
  const { t } = useTranslation('bookkeeping');
  const { activeOrgId } = useAuth();

  const { data, isLoading } = useQuery({
    queryKey: ['bk-sales-receipts', activeOrgId],
    queryFn: () =>
      api<{ sales_receipts: SalesReceipt[] }>('/bookkeeping/sales/sales-receipts?limit=50'),
    enabled: !!activeOrgId,
  });

  const receipts = data?.sales_receipts ?? [];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold">{t('sales.sales_receipts.title')}</h1>
        <Link
          to="/app/bookkeeping/sales/sales-receipts/new"
          className="rounded bg-blue-600 px-3 py-1.5 text-sm text-white hover:bg-blue-700"
        >
          + {t('sales.sales_receipts.new')}
        </Link>
      </div>

      {isLoading ? (
        <p className="text-sm text-slate-500">{t('common:loading')}</p>
      ) : (
        <div className="overflow-x-auto rounded border bg-white">
          <table className="w-full text-sm">
            <thead className="border-b text-left text-xs uppercase text-slate-500">
              <tr>
                <th className="px-3 py-2">{t('sales.invoices.number')}</th>
                <th className="px-3 py-2">{t('sales.invoices.entry_date')}</th>
                <th className="px-3 py-2">{t('sales.invoices.currency')}</th>
                <th className="px-3 py-2 text-right">{t('sales.invoices.grand_total')}</th>
                <th className="px-3 py-2">{t('sales.invoices.status')}</th>
                <th className="px-3 py-2" />
              </tr>
            </thead>
            <tbody>
              {receipts.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-3 py-4 text-center text-slate-400">
                    {t('sales.sales_receipts.empty', {
                      defaultValue: 'No hay recibos de venta aún.',
                    })}
                  </td>
                </tr>
              )}
              {receipts.map((r) => (
                <tr key={r.id} className="border-b last:border-0 hover:bg-slate-50">
                  <td className="px-3 py-2 font-mono text-xs">
                    <Link
                      to={`/app/bookkeeping/sales/sales-receipts/${r.id}`}
                      className="text-blue-600 hover:underline"
                    >
                      {r.number}
                    </Link>
                  </td>
                  <td className="px-3 py-2">{r.entry_date}</td>
                  <td className="px-3 py-2">{r.currency}</td>
                  <td className="px-3 py-2 text-right font-mono">{r.grand_total}</td>
                  <td className="px-3 py-2">
                    <span
                      className={`rounded-sm px-1.5 py-0.5 text-xs ${STATUS_CLASSES[r.status] ?? ''}`}
                    >
                      {t(`sales.invoices.statuses.${r.status}`, { defaultValue: r.status })}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-right">
                    <Link
                      to={`/app/bookkeeping/sales/sales-receipts/${r.id}`}
                      className="text-xs text-blue-600 hover:underline"
                    >
                      {t('journalEntries.viewEdit')}
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
